import type {
  Budget, BudgetTransaction, Expense, Donation, Collection,
  BudgetDocument, BudgetHead, BudgetItem,
  HeadType, VarianceRow, VarianceStatus, Numeric
} from '../../types';
// Budget-vs-actual reporting.
//
// "Actual" is pulled from three places: budget adjustments recorded in this
// module, plus every expense and every donation/collection recorded elsewhere
// in the app. Anything booked outside the budget module is attributed to the
// General Fund, since those records carry no department.

const num = (v?: Numeric | null): number => parseFloat(String(v ?? 0)) || 0;

// Indian financial year runs April–March.
const fyOf = (d: Date): string => {
  const year = d.getFullYear();
  return d.getMonth() >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

/** The financial year today falls in. */
export const currentFinancialYear = (): string => fyOf(new Date());

/**
 * The fallback used to be the literal '2026-2027', so a record with a missing
 * or unparseable date was filed under that year no matter when it was entered —
 * correct by luck until April 2027, silently wrong after. It now defaults to
 * whichever year is current.
 */
export const getFyFromDate = (dateStr?: string, fallback?: string): string => {
  const safe = fallback || currentFinancialYear();
  if (!dateStr) return safe;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return safe;
  return fyOf(d);
};

// ---------------------------------------------------------------------------
// Hierarchical budget document (heads -> sub-items)
//
// A sub-item amount is MONTHLY unless isOneTime, so the annual figure is
// `isOneTime ? amount : amount * 12`. The backend applies the same rule in
// FinancialService — keep the two in step.
// ---------------------------------------------------------------------------

export const ROMAN_NUMERALS = [
  '', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
  'XXI', 'XXII', 'XXIII', 'XXIV', 'XXV', 'XXVI', 'XXVII', 'XXVIII', 'XXIX', 'XXX'
];

/** Roman numeral for a main head; falls back to the plain number past XXX. */
export const headLabel = (index: number): string =>
  ROMAN_NUMERALS[index + 1] || String(index + 1);

/** Sub-item letter: a, b, c... then aa, ab... past z. */
export const itemLabel = (index: number): string => {
  if (index < 26) return String.fromCharCode(97 + index);
  return `${String.fromCharCode(97 + Math.floor(index / 26) - 1)}${String.fromCharCode(97 + (index % 26))}`;
};

export const annualOf = (item: BudgetItem): number =>
  item.isOneTime ? num(item.amount) : num(item.amount) * 12;

/** The names a freshly added head or item starts with; saving them as-is is a mistake. */
export const PLACEHOLDER_HEAD = 'New Budget Head';
export const PLACEHOLDER_ITEM = 'New Item';

/**
 * Why one year of the document must not be saved, or [] when it may. A budget
 * with a blank or repeated head name cannot be matched to expenses, and a
 * placeholder row left in is almost always a slip.
 */
export const documentProblems = (fy: string, heads: BudgetHead[] = []): string[] => {
  const problems: string[] = [];
  const seenHeads = new Set<string>();
  heads.forEach((head, hi) => {
    const name = String(head.headName || '').trim();
    const label = name || `head ${hi + 1}`;
    if (!name) problems.push(`Head ${hi + 1} has no name.`);
    else if (name === PLACEHOLDER_HEAD) problems.push(`"${PLACEHOLDER_HEAD}" is still the placeholder name — rename it or delete it.`);
    const k = name.toLowerCase();
    if (k && seenHeads.has(k)) problems.push(`"${name}" appears twice — merge the two heads.`);
    seenHeads.add(k);

    const seenItems = new Set<string>();
    (head.items || []).forEach((item, ii) => {
      const itemName = String(item.name || '').trim();
      if (!itemName) problems.push(`${label}: sub-item ${ii + 1} has no name.`);
      else if (itemName === PLACEHOLDER_ITEM) problems.push(`${label}: "${PLACEHOLDER_ITEM}" is still the placeholder name.`);
      const ik = itemName.toLowerCase();
      if (ik && seenItems.has(ik)) problems.push(`${label}: "${itemName}" appears twice.`);
      seenItems.add(ik);
      if (num(item.amount) < 0) problems.push(`${label} › ${itemName || `sub-item ${ii + 1}`}: the amount cannot be negative.`);
    });
  });
  return problems.map((p) => `FY ${fy}: ${p}`);
};

export interface EnrichedHead extends BudgetHead {
  headAnnual: number;
  headActual: number;
  /** Part of headActual booked under the head against no named line. */
  headOther: number;
  /** This head's share of the year's grand annual total, 0-100. */
  percentOfTotal: number;
}

export interface BudgetDocumentTotals {
  heads: EnrichedHead[];
  grandTotalAnnual: number;
  grandTotalActual: number;
  variance: number;
}

/**
 * Per-head annual/actual subtotals plus the grand totals for one year.
 *
 * `actualOf` supplies what a line has really been spent, counted from the
 * expenses booked against it. Without it the stored `item.actual` is used, which
 * is only meaningful for a budget written before spend was counted — the caller
 * passing the lookup is what keeps the head totals agreeing with the rows above
 * them.
 */
export const enrichHeads = (
  heads: BudgetHead[] = [],
  actualOf?: (headName?: string, itemName?: string) => number,
  otherOf?: (headName?: string) => number
): BudgetDocumentTotals => {
  let grandTotalAnnual = 0;
  let grandTotalActual = 0;

  const withSubtotals = heads.map((head) => {
    let headAnnual = 0;
    let headActual = 0;
    (head.items || []).forEach((item) => {
      headAnnual += annualOf(item);
      headActual += actualOf ? actualOf(head.headName, item.name) : num(item.actual);
    });
    // Spend booked under the head against no named line still belongs to the
    // head, so its subtotal equals the expense register.
    const headOther = otherOf ? otherOf(head.headName) : 0;
    headActual += headOther;
    grandTotalAnnual += headAnnual;
    grandTotalActual += headActual;
    return { ...head, headAnnual, headActual, headOther, percentOfTotal: 0 };
  });

  // Shares need the grand total, so they are filled in on a second pass.
  const enriched = withSubtotals.map((head) => ({
    ...head,
    percentOfTotal: grandTotalAnnual > 0 ? (head.headAnnual / grandTotalAnnual) * 100 : 0
  }));

  return {
    heads: enriched,
    grandTotalAnnual,
    grandTotalActual,
    variance: grandTotalAnnual - grandTotalActual
  };
};

/**
 * Copies a year's heads into a new year, optionally inflating every amount.
 * Ids are regenerated so the clone does not overwrite the source rows, and
 * `actual` resets because no spend has happened in the new year yet.
 */
export const cloneYear = (
  heads: BudgetHead[] = [],
  increasePercent = 0,
  newId: () => string = () => Math.random().toString(36).slice(2, 11)
): BudgetHead[] =>
  heads.map((head) => ({
    ...head,
    id: newId(),
    items: (head.items || []).map((item) => ({
      ...item,
      id: newId(),
      amount: increasePercent !== 0
        ? Math.round(num(item.amount) * (1 + increasePercent / 100))
        : num(item.amount),
      actual: 0
    }))
  }));

/**
 * Stamps displayOrder from the current visual order across every year, so the
 * server returns the rows in the order the user arranged them.
 */
export const withDisplayOrder = (document: BudgetDocument): BudgetDocument => {
  const ordered: BudgetDocument = {};
  Object.keys(document).forEach((year) => {
    ordered[year] = (document[year] || []).map((head, headIndex) => ({
      ...head,
      displayOrder: headIndex,
      items: (head.items || []).map((item, itemIndex) => ({
        ...item,
        displayOrder: itemIndex
      }))
    }));
  });
  return ordered;
};

/** "2026-2027" -> "2027-2028"; empty string when the input is not a FY range. */
export const nextFinancialYear = (fy: string): string => {
  const parts = String(fy || '').split('-');
  if (parts.length !== 2) return '';
  const [start, end] = parts.map((p) => parseInt(p, 10));
  if (isNaN(start) || isNaN(end)) return '';
  return `${start + 1}-${end + 1}`;
};

export interface BudgetSources {
  budgets: Budget[];
  transactions: BudgetTransaction[];
  expenses: Expense[];
  incomes: Array<Donation | Collection>;
}

export const summariseTotals = ({
  budgets, transactions, expenses, incomes
}: BudgetSources) => {
  let totalBudget = 0;
  let actualIncome = 0;
  let actualExpense = 0;

  budgets.forEach((b) => {
    if (b.headType === 'Expense' && b.workflowStatus === 'Approved') totalBudget += num(b.amount);
  });
  transactions.forEach((t) => {
    if (t.type === 'Income') actualIncome += num(t.amount);
    if (t.type === 'Expense') actualExpense += num(t.amount);
  });
  expenses.forEach((e) => { actualExpense += num(e.amount); });
  incomes.forEach((i) => { actualIncome += num(i.amount); });

  return { totalBudget, actualIncome, actualExpense, availableBalance: totalBudget - actualExpense };
};

// Colour bands differ by direction: under-spending an expense head is good,
// under-collecting an income head is bad.
//
// A head with no approved budget has no percentage to band, and zero used to be
// passed in — which cleared the "at most 85% of budget" test and painted money
// spent against an unbudgeted head green. Those rows are exactly the ones that
// need flagging, so they are judged on whether anything moved at all.
const statusFor = (
  type: HeadType | undefined,
  pct: number,
  hasBudget: boolean,
  actual: number
): VarianceStatus => {
  if (!hasBudget) return actual > 0 ? 'bad' : 'good';
  if (type === 'Income') return pct >= 100 ? 'good' : pct >= 75 ? 'warn' : 'bad';
  return pct <= 85 ? 'good' : pct <= 100 ? 'warn' : 'bad';
};

export const buildVarianceReport = ({
  budgets, transactions, expenses, incomes
}: BudgetSources): VarianceRow[] => {
  type Bucket = { head: string; type: HeadType; dept: string; fy: string; budget: number; actual: number };
  const rows: Record<string, Bucket> = {};

  const bucket = (
    head: string | undefined,
    dept: string | undefined,
    fy: string | undefined,
    type: HeadType | undefined
  ): Bucket => {
    const key = `${head}_${dept}_${fy}`;
    if (!rows[key]) {
      rows[key] = {
        head: head as string, type: (type || 'Expense') as HeadType,
        dept: dept as string, fy: fy as string, budget: 0, actual: 0
      };
    }
    return rows[key];
  };

  budgets
    .filter((b) => b.workflowStatus === 'Approved')
    .forEach((b) => { bucket(b.head, b.department, b.fy, b.headType).budget += num(b.amount); });

  transactions.forEach((t) => { bucket(t.head, t.department, t.fy, t.type).actual += num(t.amount); });

  expenses.forEach((e) => {
    bucket(e.category, 'General Fund', getFyFromDate(e.date), 'Expense').actual += num(e.amount);
  });

  incomes.forEach((i) => {
    bucket(
      (i as Donation).purpose || (i as Collection).source,
      'General Fund', getFyFromDate(i.date), 'Income'
    ).actual += num(i.amount);
  });

  return Object.values(rows)
    .map((row) => {
      const isIncome = row.type === 'Income';
      const hasBudget = row.budget > 0;
      const variancePct = hasBudget ? (row.actual / row.budget) * 100 : 0;
      const varianceAmt = isIncome ? row.actual - row.budget : row.budget - row.actual;
      return {
        ...row,
        varianceAmt,
        variancePct,
        status: statusFor(row.type, variancePct, hasBudget, row.actual)
      };
    })
    .sort((a, b) =>
      String(a.type).localeCompare(String(b.type)) || String(a.head).localeCompare(String(b.head))
    );
};
